import { defineComponent as _defineComponent } from 'vue'
import '@/scss/sidebar-menu.scss';
import './SidebarArticleItem.scss';
import { WorkspaceViewArticleItem } from '@/@types/WorkspaceViewArticleItem';

import SidebarNestedArticleItem from '@/components/layout/sidebars/SidebarArticleItem/SidebarNestedArticleItem.vue';
import MoreButton from '@/components/layout/sidebars/SidebarArticleItem/MoreButton.vue';
import logger from '@/other/Logger';
import articleService from '@/services/article/ArticleService';

import store from '@/store';
import helper from '@/helper';
import EventBus from '@/EventBus';
import apiService from '@/services/ApiService';
import tooltip from '@/services/ui-providers/Tooltip';
import notificationService from '@/services/NotificationService';
import slugify from 'slugify';

import githubIcon from '@/assets/img/integrations/github.svg';
import bitbucketIcon from '@/assets/img/integrations/bitbucket.svg';
import confluenceIcon from '@/assets/img/integrations/confluence.svg';
import gdocsIcon from '@/assets/img/integrations/gdocs.svg';
import { computed, defineAsyncComponent, onMounted, ref } from 'vue';
import { dispatchTriggerSync, dispatchDisconnectConnector } from '@/store/dispatchers/connectorDispatchers';
import { dispatchOpenSelectedArticle } from '@/store/dispatchers/workspaceViewDispatchers';
import { commitSetSelectedArticle } from '@/store/commits/workspaceViewCommits';
import { dispatchGetPersonalArticlesCount } from '@/store/dispatchers/uiDispatchers';
import { workspaceService } from '@/services/workspace/WorkspaceService';
import exportArticlePdfInBrowser from '@/services/article/pdf/exportArticlePdfInBrowser';
import navigationService from '@/services/NavigationService';
import urlService from '@/services/UrlService';

import { ArticleEditMode } from '@/@types/ArticleEditMode';
import { WorkspaceOrder } from '@/@types/WorkspaceOrder';
import { WorkspaceView } from '@/@types/WorkspaceView';
import { Workspace } from '@/models/Workspace';
import { ConnectorConfig } from '@/models/ConnectorConfig';
import { Loading } from 'element-ui';


export default /*#__PURE__*/_defineComponent({
  __name: 'SidebarArticleItem',
  props: {
    item: null,
    nestedOrder: null,
    recalculateSidebarHeight: null,
    period: { default: '' },
    nested: { default: 1 },
    paragraphProgress: { default: 0 },
    workspace: null,
    workspaceView: null,
    showArchivedArticles: { type: Boolean },
    articleMode: null,
    isMobileView: { type: Boolean },
    isSharedMode: { type: Boolean },
    expanded: null
  },
  emits: ['toggle-expand', 'navigate-to-first-article'],
  setup(__props: any, { emit }) {

const props = __props as { item: WorkspaceViewArticleItem, nestedOrder?: WorkspaceOrder[], recalculateSidebarHeight: () => void, period: string, nested: number, paragraphProgress: number, workspace: Workspace, workspaceView: WorkspaceView, showArchivedArticles: boolean, articleMode?: ArticleEditMode, isMobileView: boolean, isSharedMode: boolean, expanded: string[] }

const SOURCE_ICONS: Record<string, string> = {
  GITHUB: githubIcon,
  BITBUCKET: bitbucketIcon,
  CONFLUENCE: confluenceIcon,
  GDRIVE: gdocsIcon,
};
const sourceIconUrl = (type: string | null): string | null => (type ? SOURCE_ICONS[type.toUpperCase()] ?? null : null);
const DisconnectConfirmDialog = defineAsyncComponent(
  () => import('@/components/connectors/DisconnectConfirmDialog.vue')
);
const ConnectorEditDialog = defineAsyncComponent(() => import('@/components/connectors/ConnectorEditDialog.vue'));
const ReconnectDialog = defineAsyncComponent(() => import('@/components/connectors/ReconnectDialog.vue'));



const loader = ref<any>(Loading);
const articleIds = ref<string[]>([]);
const parentArticleId = ref('');
const isTitleFocused = ref(false);
const showDisconnectDialog = ref(false);
const showEditDialog = ref(false);
const showReconnectDialog = ref(false);
const isDisconnecting = ref(false);



const currentConnectorConfig = computed((): ConnectorConfig | undefined => {
  if (!props.item.sourceConnectorId) return undefined;
  return store.state.connector.connectors.find((c: ConnectorConfig) => c.id === props.item.sourceConnectorId);
});

const handleToggle = (article: WorkspaceViewArticleItem) => {
  props.recalculateSidebarHeight();
  emit('toggle-expand', article);
};

const hasNestedOrder = computed(() => props.nestedOrder?.length > 0);

const isShowExtraSpace = computed(() => !props.item.isHasVideo && !hasNestedOrder.value);

const isNestedItem = computed(() => props.item.isHasVideo || hasNestedOrder.value);

const isActiveItem = () => {
  return props.workspaceView.selectedArticle && props.workspaceView.selectedArticle.id === props.item.id;
};

const handleTitleFocus = (e: any, title: string) => {
  tooltip.handleFocusIn(e, title);
  isTitleFocused.value = true;
};

const removeTooltips = () => {
  tooltip.clearTooltips();
  isTitleFocused.value = false;
};

const openArticle = async (article: WorkspaceViewArticleItem, newTab?: boolean) => {
  loader.value = Loading.service({});
  try {
    await dispatchOpenSelectedArticle({ articleId: article.id, newTab });
  } finally {
    loader.value.close();
  }
};

const handleClick = (e: PointerEvent | MouseEvent) => {
  e.preventDefault();
  const target = e.target as HTMLElement;
  const isModifierPressed = e.metaKey || e.ctrlKey || e.button === 1;

  const isAlreadyOpened = props.item && props.item?.id === props.workspaceView.selectedArticle?.id;

  if (target.closest('.article-more-button')) {
    return;
  }

  if (target.closest('.article-item-options') || (isAlreadyOpened && !isModifierPressed)) {
    if (isAlreadyOpened) handleToggle(props.item);
    return;
  }

  if (props.isMobileView && !isModifierPressed) {
    removeTooltips();
    store.commit('toggleMainSidebar', false);
  }

  openArticle(props.item, isModifierPressed);
};

const handleDuplicateArticle = async () => {
  loader.value = Loading.service({ text: 'Duplicating article' });

  const article = props.item;

  try {
    await articleService.duplicateArticle(article.id);
  } catch (err) {
    apiService.handleResponseError(err);
  } finally {
    loader.value = Loading.service({}).close();
  }
};

const handleMergeArticle = () => {
  EventBus.$emit('merge-article', props.item);
};

const createSubArticle = () => {
  EventBus.$emit('create-sub-article', props.item);
};

const handleCreateGroup = () => {
  EventBus.$emit('create-group', props.item);
};

const handleReorderArticles = () => {
  EventBus.$emit('reorder-articles');
};

const handleCopyArticleUrl = async () => {
  if (props.workspace && props.item) {
    const baseUrl = location.href.replace(location.hash, '');

    const url = `${baseUrl}#/b/${props.workspace.shortId}/${props.workspace.slug}/${props.item.shortId}/${slugify(
      props.item.title
    )}`;
    helper.copyToClipboard(url);
    notificationService.success('Article link copied to clipboard');
  } else if (props.isSharedMode) {
    helper.copyToClipboard(location.href);
    notificationService.success('Article link copied to clipboard');
  } else {
    notificationService.warning('Cannot copy link to this article');
  }
};

const getValue = (item: any) => {
  if (item.items && item.items.length) {
    item.items.forEach((element: any) => {
      articleIds.value.push(element.value);
      if (element.items.length) {
        getValue(element);
      }
    });
  } else {
    articleIds.value.push(item.value);
  }
};

const handleNestedOrder = () => {
  if (props.nestedOrder.length) {
    props.nestedOrder.forEach((item: any) => {
      articleIds.value.push(item.value);
      if (item.items.length) {
        getValue(item);
      }
    });
  }
};

const getParentArticle = (order: any[], isArchived: boolean, articleId: string) => {
  if (order.length && isArchived) {
    order.forEach((item: any) => {
      if (item.value === articleId || parentArticleId.value) return;

      parentArticleId.value = workspaceService.getParentArticle(item, articleId);
    });
  }
};

const saveArchivedArticle = async (order: any[], article: WorkspaceViewArticleItem) => {
  workspaceService.setState('showArchivedArticles', false);

  const found = workspaceService.checkArticleOrder(order, article.id);
  const newOrder = workspaceService.removeItemFromTree(order, article.id);

  newOrder.push(found);
  await workspaceService.saveArticlesOrder(newOrder);
};

const archiveArticle = async (article: WorkspaceViewArticleItem, isArchived: boolean) => {
  // if (isArchived === true) {
  //   commitSetSelectedArticle(article);
  // }

  try {
    await apiService.handleArchiveArticle(articleIds.value, article.isArchived);
    notificationService.success(`The article has been successfully ${isArchived ? 'unarchived' : 'archived'}!`);
    isArchived && (await dispatchGetPersonalArticlesCount());
  } catch (error) {
    apiService.handleResponseError(error);
  }
};

const handleToggleArchiveArticle = async () => {
  const article = props.item;
  parentArticleId.value = null;
  articleIds.value = [];
  const order = props.workspaceView.workspace.order;
  const isArchived = article.isArchived;
  article.isArchived = !isArchived;

  handleNestedOrder();

  articleIds.value.push(article.id);

  getParentArticle(order, isArchived, article.id);

  const parentArticleArchived = props.workspaceView.articles.filter(
    (element) => element.id === parentArticleId.value && element.isArchived === true
  );

  if (parentArticleArchived.length) {
    await saveArchivedArticle(order, article);
  }

  props.workspaceView.articles.forEach((element) => {
    if (articleIds.value.includes(element.id)) {
      element.isArchived = !isArchived;
    }
  });

  await archiveArticle(article, isArchived);
};

const handleExportPdfArticle = async () => {
  loader.value = Loading.service({ text: 'Generating PDF...' });
  try {
    await exportArticlePdfInBrowser(props.item);
    notificationService.success('Successfully generated PDF');
  } catch (error) {
    logger.error(error);
    notificationService.error('Failed to generate PDF');
  } finally {
    loader.value = Loading.service({}).close();
  }
};

// --- Connector management handlers ---

const handleSyncNow = async () => {
  if (!props.item.sourceConnectorId) return;
  // Optimistic UI: set badge to SYNCING (MGMT-04)
  (props.item as Record<string, unknown>).syncStatus = 'SYNCING';
  try {
    await dispatchTriggerSync(props.item.sourceConnectorId);
  } catch (err) {
    (props.item as Record<string, unknown>).syncStatus = 'ERROR';
  }
};

const handleEditIntegration = () => {
  if (!props.item.sourceConnectorId) return;
  showEditDialog.value = true;
};

const handleDisconnectRequest = () => {
  if (!props.item.sourceConnectorId) return;
  showDisconnectDialog.value = true;
};

/** Delete on external article = Disconnect + Remove All (per CONTEXT.md locked decision) */
const handleDeleteAsDisconnect = async () => {
  if (!props.item.sourceConnectorId) return;
  try {
    await dispatchDisconnectConnector(props.item.sourceConnectorId);
  } catch (err) {
    logger.error('Delete (disconnect + remove all) failed:', err);
  }
};

const handleDisconnectConfirm = async (keepArticles: boolean) => {
  if (!props.item.sourceConnectorId) return;
  isDisconnecting.value = true;
  try {
    const result = await dispatchDisconnectConnector(props.item.sourceConnectorId, keepArticles);
    if (result?.workspaceOrder) {
      store.commit('setWorkspaceOrder', result.workspaceOrder);
    }
    showDisconnectDialog.value = false;
    await workspaceService.loadArticlesItems();
    await dispatchGetPersonalArticlesCount();
    if (keepArticles && props.item.id) {
      // Articles kept as stale — refresh current article in editor
      await dispatchOpenSelectedArticle({ articleId: props.item.id });
    } else {
      // Articles deleted — navigate to workspace view, which auto-selects first article
      commitSetSelectedArticle(null);
      await navigationService.navigate(urlService.getWorkspaceViewRoute(props.workspace));
    }
  } catch (err) {
    logger.error('Disconnect failed:', err);
  } finally {
    isDisconnecting.value = false;
  }
};

const handleDisconnectCancel = () => {
  showDisconnectDialog.value = false;
};

const handleEditConfirm = () => {
  showEditDialog.value = false;
};

const handleEditCancel = () => {
  showEditDialog.value = false;
};

const handleReconnect = () => {
  showReconnectDialog.value = true;
};

const handleReconnectCancel = () => {
  showReconnectDialog.value = false;
};

const handleReconnectSuccess = async (result: Record<string, unknown>) => {
  showReconnectDialog.value = false;
  if (result?.workspaceOrder) {
    store.commit('setWorkspaceOrder', result.workspaceOrder);
  }
  await workspaceService.loadArticlesItems();
  if (props.item.id) {
    await dispatchOpenSelectedArticle({ articleId: props.item.id });
  }
};

onMounted(() => {
  if (workspaceService.workspaceView.isSharedMode) return;
  if (workspaceService.workspaceView.showArchivedArticles) return;
  const archivedArticles = document.querySelectorAll('.archived');

  if (archivedArticles) {
    archivedArticles.forEach((article) => {
      article.classList.add('hideArchivedArticle');
    });
  }
});

return { __sfc: true,SOURCE_ICONS, sourceIconUrl, DisconnectConfirmDialog, ConnectorEditDialog, ReconnectDialog, props, loader, articleIds, parentArticleId, isTitleFocused, showDisconnectDialog, showEditDialog, showReconnectDialog, isDisconnecting, emit, currentConnectorConfig, handleToggle, hasNestedOrder, isShowExtraSpace, isNestedItem, isActiveItem, handleTitleFocus, removeTooltips, openArticle, handleClick, handleDuplicateArticle, handleMergeArticle, createSubArticle, handleCreateGroup, handleReorderArticles, handleCopyArticleUrl, getValue, handleNestedOrder, getParentArticle, saveArchivedArticle, archiveArticle, handleToggleArchiveArticle, handleExportPdfArticle, handleSyncNow, handleEditIntegration, handleDisconnectRequest, handleDeleteAsDisconnect, handleDisconnectConfirm, handleDisconnectCancel, handleEditConfirm, handleEditCancel, handleReconnect, handleReconnectCancel, handleReconnectSuccess, SidebarNestedArticleItem, MoreButton }
}

})